import { Form } from "antd/es";
import * as React from "react";

/**
 * Which settings have been edited since the configuration was opened.
 *
 * The editor is one long form behind a category rail, and only one category is drawn at a
 * time — so without a marker the answer to "what did I just change?" is a walk through
 * fifty-one categories. Two things read this: the rail, which dots the categories holding
 * an edit, and the field label, which turns orange where the edit actually is. Both go
 * away on save, because the snapshot they are measured against is retaken there.
 *
 * It is deliberately NOT the same question as `parentChildDifference`, which asks what
 * this configuration overrides in the workspace one. A value can be changed-but-inherited
 * (edited back to the parent's value) or unchanged-but-overriding (saved that way weeks
 * ago), so the rail draws them as two different marks.
 */

/** A form path — `["apiConfig", "cacheSettingsId"]` — as one comparable string. */
export const pathKey = (formInputName: readonly string[]): string => formInputName.join(".");

/** The slices the editor's form is built from; the root-level keys are not fields. */
const TRACKED_SLICES: string[] = ["apiConfig", "webConfig", "iosConfig", "androidConfig", "electronConfig"];

/**
 * The exception to "root-level keys are not fields": a root key a control genuinely edits,
 * rather than a slice's own setting. `swaggerUiMode` is the one today — it picks Default vs
 * Custom credentials and has nowhere else to live, since it is UI-only and not part of
 * `IConfig` at all. Listed explicitly rather than tracked generically, so a stray root key
 * (`name`, `isKuikaCloud`, …) never lights up a mark nobody drew.
 */
const TRACKED_ROOT_KEYS: string[] = ["swaggerUiMode"];

export interface IConfigFormValues {
  [slice: string]: IConfigSliceValues | string | undefined;
}

export interface IConfigSliceValues {
  [key: string]: unknown;
}

/**
 * The paths whose value differs between the form and the snapshot taken when the
 * configuration was loaded (or last saved).
 *
 * Deep equality, because two of the tracked values are lists — the datasource bindings and
 * the desktop target platforms — and a fresh array is never the same reference as the one
 * in the snapshot.
 */
export const changedPathsBetween = (current: IConfigFormValues, snapshot: IConfigFormValues): Set<string> => {
  const changed: Set<string> = new Set<string>();

  TRACKED_SLICES.forEach((slice: string): void => {
    // A tracked slice's value is always slice-shaped — only a root key ever holds a bare
    // string — so this narrowing is safe for exactly the keys this loop visits.
    const currentSlice: IConfigSliceValues = (current[slice] as IConfigSliceValues | undefined) ?? {};
    const snapshotSlice: IConfigSliceValues = (snapshot[slice] as IConfigSliceValues | undefined) ?? {};
    const keys: Set<string> = new Set<string>([...Object.keys(currentSlice), ...Object.keys(snapshotSlice)]);

    keys.forEach((key: string): void => {
      if (!isSameValue(currentSlice[key], snapshotSlice[key])) changed.add(`${slice}.${key}`);
    });
  });

  TRACKED_ROOT_KEYS.forEach((key: string): void => {
    if (!isSameValue(current[key], snapshot[key])) changed.add(key);
  });

  return changed;
};

/**
 * Value equality for a form field.
 *
 * `undefined`, `null` and `""` all mean "no value" to these controls — a select cleared
 * back to empty must not read as an edit just because antd handed back `""` where the
 * configuration had `null`.
 */
const isSameValue = (a: unknown, b: unknown): boolean => {
  if (isEmpty(a) && isEmpty(b)) return true;
  if (Array.isArray(a) || Array.isArray(b)) return JSON.stringify(a) === JSON.stringify(b);
  return a === b;
};

/**
 * `undefined`, `null`, `""` and `[]` are all "no value" to these controls. The array case is
 * what a checkbox group hands back for an empty selection — Electron's Linux package formats
 * category only mounts once Linux is a target, so it is absent from the snapshot entirely
 * until then; without this, selecting Linux would mount the field at `[]` and read as a
 * change against a snapshot that never saw the key at all. The same rule applies to every
 * other array-valued field: a REST datasource binding list saved as `null` and one saved as
 * `[]` are the same "nothing bound" — checked before the two are compared by contents below.
 */
const isEmpty = (value: unknown): boolean =>
  value === undefined || value === null || value === "" || (Array.isArray(value) && value.length === 0);

export interface IConfigChangeRegistry {
  /** The paths edited since the configuration was opened. */
  changedPaths: ReadonlySet<string>;
  /** A field announcing which category it belongs to, so the rail can dot that category. */
  registerField: (categoryId: string, path: string) => void;
  unregisterField: (categoryId: string, path: string) => void;
  /**
   * Put these paths back to the value the snapshot holds.
   *
   * The mark is the undo: the snapshot already holds what each of them was, so pointing at
   * an edit is also the shortest way to take it back — a label to drop one field, the
   * rail's dot to drop everything in the category.
   */
  revertPaths: (paths: string[]) => void;
  /** The paths a category owns, for the rail's revert. */
  categoryPaths: (categoryId: string) => string[];
}

const EMPTY_REGISTRY: IConfigChangeRegistry = {
  changedPaths: new Set<string>(),
  registerField: (): void => {},
  unregisterField: (): void => {},
  revertPaths: (): void => {},
  categoryPaths: (): string[] => []
};

/**
 * One path's value in the snapshot, ready to be written back through the form writer.
 *
 * A root key's path has no second segment — `snapshotValueAt(snapshot, "swaggerUiMode")` —
 * so it reads the value straight off the snapshot rather than out of a slice.
 */
export const snapshotValueAt = (snapshot: IConfigFormValues, path: string): unknown => {
  const [slice, key] = path.split(".");
  if (key === undefined) return snapshot[slice];
  return (snapshot[slice] as IConfigSliceValues | undefined)?.[key];
};

export const ConfigChangeContext: React.Context<IConfigChangeRegistry> =
  React.createContext<IConfigChangeRegistry>(EMPTY_REGISTRY);

/** What `ConfigSection` tells the parts inside it about where they are. */
export interface IConfigSectionScope {
  /** Undefined for a section of the pane's own column, which is not a rail category. */
  categoryId?: string;
  /** The section's title, so a field can name the place it lives in without being told. */
  title: string;
  /** False for every category except the one the rail is pointing at. */
  active: boolean;
}

/** For a field drawn outside any section — nothing registers, nothing dots. */
export const EMPTY_SECTION_SCOPE: IConfigSectionScope = { title: "", active: true };

/**
 * The section a field is being drawn inside. `ConfigSection` is what sets it.
 *
 * It carries the section's title and whether the rail is pointing at it as well as its id,
 * because the same answer serves three readers: the rail's dots need the id, the search
 * index needs the title to name the place a setting lives, and a reveal needs to know the
 * section is actually on screen before scrolling to something inside it.
 */
export const ConfigCategoryContext: React.Context<IConfigSectionScope> =
  React.createContext<IConfigSectionScope>(EMPTY_SECTION_SCOPE);

/**
 * The form path a `ConfigField` wraps.
 *
 * Nearly three hundred of them are written as `<ConfigField label="…"><Form.Item name={…}>`,
 * so the path is already there, one level down — reading it beats adding a second copy to
 * every call site and letting the two drift. `ConfigSettingSelect` renders its own
 * `Form.Item` and passes the path explicitly instead.
 */
export const readFieldPath = (explicit: string[] | undefined, children: React.ReactNode): string | undefined => {
  if (explicit) return pathKey(explicit);
  return findFormItemPath(children, 0);
};

const MAX_DEPTH: number = 3;

const findFormItemPath = (node: React.ReactNode, depth: number): string | undefined => {
  if (depth > MAX_DEPTH) return undefined;

  let found: string | undefined;
  React.Children.forEach(node, (child: React.ReactNode): void => {
    if (found !== undefined || !React.isValidElement(child)) return;

    if (child.type === Form.Item && Array.isArray(child.props.name)) {
      found = pathKey(child.props.name);
      return;
    }
    found = findFormItemPath(child.props.children, depth + 1);
  });
  return found;
};
